/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import * as _ from 'lodash';

import template from './list-column-selector.component.html';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {UserProfileResource} from '../../../components/api/resources/resource/user-profile.resource';
import {LocalUserProfile} from '../../../components/session/local-user-profile';

@Component({
  standalone: false,
  selector: 'adn-list-column-selector',
  template
})
export class ListColumnSelectorComponent implements OnInit, OnChanges {
  @Input() allFields: any[];
  @Input() defaultFields: any[];
  @Input() sel: any;
  @Input() changeHook: any;
  @Input() profileParam = '';
  @Input() view = '';

  private localUser: any;

  constructor(
    private readonly adnLog: AdnLogService,
    @Inject(UserProfileResource) private readonly UserProfile: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any
  ) {
  }

  ngOnInit(): void {
    this.localUser = this.localUserProfile.get();
    this.initializeSelection();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.defaultFields || changes.allFields) {
      this.initializeSelection();
    }
  }

  private initializeSelection(): void {
    if (_.isString(this.sel)) {
      this.adnLog.warn('ListColumnSelector: expected object binding for sel, got string:', this.sel);
      this.sel = {};
    }
    this.sel = this.sel || {};

    if (!this.defaultFields?.length) {
      return;
    }

    if (_.isNil(this.sel.selFields) || _.isNil(this.sel.outputFields)) {
      this.sel.outputFields = _.cloneDeep(this.defaultFields);
      this.sel.selFields = _.map(this.defaultFields, (l: any) => this.fieldId(l));
    }

    if (this.view === 'SLIM') {
      return;
    }

    const profile = this.localUserProfile.get().profile;
    const profileSelFieldIds = _.get(profile[this.profileParam], 'selFieldIds') || [];
    if (profileSelFieldIds.length > 0 && this.allFields?.length) {
      const verifiedSelFields = _.filter(this.allFields, (l: any) => {
        return profileSelFieldIds.indexOf(this.fieldId(l)) > -1;
      });
      if (verifiedSelFields.length > 0) {
        this.sel.selFields = _.map(verifiedSelFields, (l: any) => this.fieldId(l));
        this.sel.outputFields = verifiedSelFields;
        this.fieldsUpdated();
      }
    }
  }

  fieldLabelKey(field: any): string {
    return 'label.' + (field.otherLabel || field.label || field.param || field);
  }

  isFieldSelected(field: any): boolean {
    return (this.sel.selFields || []).indexOf(this.fieldId(field)) > -1;
  }

  onFieldSelectionChange(field: any, checked: boolean): void {
    const fieldId = this.fieldId(field);
    const selected = new Set(this.sel.selFields || []);
    if (checked) {
      selected.add(fieldId);
    } else {
      selected.delete(fieldId);
    }
    this.sel.selFields = _.map(_.filter(this.allFields, (l: any) => selected.has(this.fieldId(l))), (l: any) => this.fieldId(l));
    this.updateFields();
  }

  private fieldId(field: any): string {
    return field.param || field.label || field;
  }

  private fieldsUpdated(): void {
    if (_.isFunction(_.get(this, ['changeHook', 'fieldChange']))) {
      this.changeHook.fieldChange();
    }
  }

  private updateFields(): void {
    this.sel.outputFields = _.filter(this.allFields, (l: any) => {
      return this.sel.selFields.indexOf(this.fieldId(l)) > -1;
    });
    this.fieldsUpdated();

    if (_.isString(this.profileParam) && this.profileParam) {
      const profile = this.localUser.profile;
      profile[this.profileParam] = {
        selFieldIds: this.sel.selFields
      };
      this.UserProfile.save({
        id: this.localUser.userId,
        profile: _.pick(profile, [this.profileParam])
      }, (apiUser: any) => {
        this.localUser = this.localUserProfile.safeSaveProfile(this.profileParam, apiUser.profile[this.profileParam]);
      });
    } else {
      this.adnLog.warn("Can't save settings because of a faulty profileParam of " + this.profileParam);
    }
  }
}
